/**
 * Reopening a WHERE / HAVING pill as the structured chip: `resolveEditPredicate`
 * parses the predicate (plus the PostgreSQL loose-FK JSON fallback below) and
 * resolves its column; `enrichParsedCondition` then turns the parsed condition
 * into the one Table Explorer's chip shows for that column — boolean switch
 * values, enum and lookup operators, JSON enum / lookup sub-operators — via the
 * shared `upgradeReopenedCondition`, which only changes it when the SQL stays
 * byte-identical. The parser itself has no column or MetaInfo context: `=`,
 * a single enum pick and a lookup write the same text.
 */
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import { databaseEngineIdToSqlEngine } from '../../../../../core/engines/databaseEngineIdToSqlEngine';
import { sqlEngineToDatabaseEngineId } from '../../../../../core/engines/sqlEngineToDatabaseEngineId';
import type { LooseRelationship } from '../../../../../types/electron';
import { unwrapOuterParens } from '../../../../sql-resolver/edit/wholePredicate';
import type { VqeEditContext } from '../../edit/editContext';
import { findLooseRelationshipsForColumn, resolveActiveLooseRel } from '../../../common/utils/looseFkResolver';
import type { FilterConditionParsed } from '../../../common/utils/predicateToFilterCondition';
import { predicateToFilterCondition } from '../../../common/utils/predicateToFilterCondition';
import { matchPgLookupExtract } from '../../../common/utils/predicateJsonBranchPg';
import { upgradeReopenedCondition } from '../../../common/utils/reopenedConditionUpgrade';
import type { ResolvedFilterColumn } from './useVqeFilterColumns';

function looseRelsFor(resolved: ResolvedFilterColumn): readonly LooseRelationship[] {
  return findLooseRelationshipsForColumn(
    resolved.jsonContext?.metaInfo?.looseRelationships,
    resolved.schema ?? '',
    resolved.table,
    resolved.column.name,
  );
}

/**
 * PG-only fallback for the loose-FK JSON-path shape the generic parser
 * (`predicateToFilterCondition` → `parseJsonPredicate` → `parsePgJsonPredicate`)
 * never recognizes: PG's `json` operator emit ALWAYS routes the field through
 * `coerceForDeclaredOnText` (adds `(field)::jsonb`), but `lookup`+`sourcePath`
 * extracts from the RAW field (`pgsqlExtractText` in `jsonFilterSql.ts` takes
 * whatever `quotedField` it's given — `filterWhereBuilder.ts` passes the bare
 * field, `buildJsonConditionSql` passes the coerced one). The two shapes
 * genuinely differ on PG, so `predicateToFilterCondition(..., 'pgsql')`
 * correctly returns `null` for this predicate — this fallback is tried
 * ONLY in that null case, resolving the column from the raw regex match
 * (no `parsed`/`resolved` exists yet at that point in `EditBuilder`).
 *
 * Round-trip safety: this ALWAYS produces a `lookup` condition (never a bare
 * `path_eq` json chip) — `lookup`'s re-emit also skips the `::jsonb` cast
 * (`extractTextSql(engine, quotedField, sourcePath)` with the raw field), so
 * `emit(this result) === initialPredicate` byte-for-byte. If no matching
 * loose FK is found, this returns `null` and the predicate falls back to raw
 * text — never a best-guess chip.
 */
export function enrichPgJsonLookupFromRaw(
  initialPredicate: string,
  engine: DatabaseEngineId,
  resolveByField: (fieldText: string) => ResolvedFilterColumn | null,
): { parsed: FilterConditionParsed; resolved: ResolvedFilterColumn } | null {
  if (engine !== 'pgsql') return null;
  const m = matchPgLookupExtract(initialPredicate.trim());
  if (!m) return null;
  const resolved = resolveByField(m.field);
  if (!resolved) return null;
  const rel = resolveActiveLooseRel(looseRelsFor(resolved), m.path);
  if (!rel) return null;
  return {
    parsed: { field: m.field, condition: { operator: 'lookup', value: m.value, sourcePath: m.path } },
    resolved,
  };
}

/**
 * Entry point used by `VqeFilterChipBuilder.tsx`'s `EditBuilder`: the parsed
 * condition as the column's chip shows it (see the module header); unchanged
 * when no upgrade writes the same SQL.
 */
export function enrichParsedCondition(
  parsed: FilterConditionParsed,
  resolved: ResolvedFilterColumn,
  engine: DatabaseEngineId,
): FilterConditionParsed {
  const condition = upgradeReopenedCondition(parsed.field, parsed.condition, resolved, engine);
  return condition === parsed.condition ? parsed : { field: parsed.field, condition };
}

/**
 * PARITY-bundle review fix — the FULL "can this predicate resolve into
 * `VqeFilterChipBuilder`'s edit-mode structured chip" check: the generic
 * parser OR (PG-only) `enrichPgJsonLookupFromRaw`'s raw-extract loose-FK
 * fallback above.
 *
 * This is the SINGLE source of truth shared by the production gates
 * (`FilterEditablePill.tsx`, `RailLeafChip.tsx`'s editing body) AND
 * `VqeFilterChipBuilder.tsx`'s own `EditBuilder` — before this fix, the two
 * gates checked ONLY `predicateToFilterCondition`, while `EditBuilder` ALSO
 * tried the PG fallback. That made the fallback UNREACHABLE in the real app
 * (the gate showed raw text before the builder ever mounted) even though a
 * component test that mounted `VqeFilterChipBuilder` directly (bypassing
 * both gates) passed — false confidence. Routing every caller through this
 * one function makes "gate says chip-eligible" and "builder can resolve"
 * structurally identical, so they can't drift apart in either direction.
 *
 * A multi-part filter is written as ONE parenthesised predicate
 * (`(<guard> AND <test>)`, sql-resolver `parenthesizeCompound`), so the
 * generic parse tries the text as written — some emitted shapes carry their
 * own outer parentheses (JSON `!=`) — and then its inside.
 *
 * Returns `null` when neither path resolves — callers fall back to raw text.
 */
export function resolveEditPredicate(
  predicateText: string,
  engine: DatabaseEngineId,
  resolveByField: (fieldText: string) => ResolvedFilterColumn | null,
): { parsed: FilterConditionParsed; resolved: ResolvedFilterColumn | null } | null {
  const sqlEngine = databaseEngineIdToSqlEngine(engine);
  const genericParsed =
    predicateToFilterCondition(predicateText, engine) ??
    (sqlEngine && predicateToFilterCondition(unwrapOuterParens(predicateText, { engine: sqlEngine }), engine));
  if (genericParsed) {
    return { parsed: genericParsed, resolved: resolveByField(genericParsed.field) };
  }
  const pgFallback = enrichPgJsonLookupFromRaw(predicateText, engine, resolveByField);
  return pgFallback ? { parsed: pgFallback.parsed, resolved: pgFallback.resolved } : null;
}

/**
 * True when a WHERE / HAVING fragment — ALL of it — is one filter the chip
 * reopens. The views ask before splitting a parenthesised group into member
 * pills or drawing subquery drill chips, so a multi-part filter the chip
 * wrote (JSON `!=`, a JSON test behind its validity guard) is one pill in the
 * Inline, Rail and UPDATE / DELETE views alike. Column-independent: the PG
 * raw-extract lookup fallback needs a resolved column and is never multi-part.
 */
export function isWholeChipFilter(text: string, editContext: VqeEditContext): boolean {
  return resolveEditPredicate(text, sqlEngineToDatabaseEngineId(editContext.service.engineId), () => null) !== null;
}
